import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Menu, X, Languages } from "lucide-react";
import { useState } from "react";

const PRIMARY_LINKS = [
  { to: "/", key: "nav.my_journey", testid: "nav-about", end: true },
  { to: "/services", key: "nav.services", testid: "nav-services" },
  { to: "/testimonials", key: "nav.testimonials", testid: "nav-testimonials" },
  { to: "/company-training", key: "nav.company_training", testid: "nav-company" },
  { to: "/how-i-work", key: "nav.how_i_work", testid: "nav-how-i-work" },
  { to: "/contact", key: "nav.contact", testid: "nav-contact" },
];

function LangToggle({ compact = false }) {
  const { lang, toggle, t } = useLang();
  return (
    <button
      onClick={toggle}
      data-testid="nav-lang-toggle"
      aria-label={t("nav.lang_label")}
      title={t("nav.lang_label")}
      className={
        compact
          ? "inline-flex items-center gap-1.5 text-sm text-[color:var(--ink-2)] hover:text-[color:var(--ink)]"
          : "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[color:var(--line-soft)] hover:border-[color:var(--accent)]/40 transition-colors text-xs uppercase tracking-widest text-[color:var(--ink-2)]"
      }
    >
      <Languages size={14} strokeWidth={1.6} />
      <span className="font-medium">{lang === "en" ? "EN" : "DA"}</span>
      <span className="text-[color:var(--ink-3)]">/</span>
      <span className="text-[color:var(--ink-3)]">{lang === "en" ? "DA" : "EN"}</span>
    </button>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const navClass = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${
      isActive
        ? "text-[color:var(--accent)]"
        : "text-[color:var(--ink-2)] hover:text-[color:var(--ink)]"
    }`;

  return (
    <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-md bg-[rgba(251,249,246,0.78)] border-b border-[color:var(--line-soft)] no-print">
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 sm:h-24 flex items-center justify-between">
        <Link to="/" data-testid="nav-brand" className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_whole-being-coach/artifacts/ozuwqyb6_IMG_1602.jpg"
            alt="YouFit by Sopfie Søndergaard"
            className="h-14 w-14 sm:h-20 sm:w-20 rounded-full object-cover ring-1 ring-[color:var(--line-soft)] shadow-sm"
          />
          <span className="hidden sm:flex items-baseline gap-1">
            <span className="font-serif text-2xl md:text-3xl tracking-tight text-[color:var(--ink)] font-medium">Youfitbysopfie</span>
            <span className="font-serif text-sm text-[color:var(--accent)] font-medium">.com</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {PRIMARY_LINKS.map((l) => (
            <NavLink
              key={l.testid}
              to={l.to}
              end={l.end}
              data-testid={l.testid}
              className={navClass}
            >
              {t(l.key)}
            </NavLink>
          ))}
          {user ? (
            <>
              {user.role === "admin" ? (
                <>
                  <Link to="/admin" data-testid="nav-admin" className="text-sm tracking-wide text-[color:var(--ink-2)] hover:text-[color:var(--ink)]">
                    {t("nav.studio")}
                  </Link>
                  <Link to="/dashboard" data-testid="nav-dashboard" className="text-sm tracking-wide text-[color:var(--ink-2)] hover:text-[color:var(--ink)]">
                    {t("nav.your_space")}
                  </Link>
                </>
              ) : (
                <Link to="/dashboard" data-testid="nav-dashboard" className="text-sm tracking-wide text-[color:var(--ink-2)] hover:text-[color:var(--ink)]">
                  {t("nav.your_space")}
                </Link>
              )}
              <button
                data-testid="nav-logout"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                className="btn-ghost !py-2 !px-4 !text-sm"
              >
                {t("nav.sign_out")}
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login" className="btn-ghost !py-2 !px-4 !text-sm">
              {t("nav.client_login")}
            </Link>
          )}
          <LangToggle />
        </nav>

        <div className="md:hidden flex items-center gap-3">
          <LangToggle compact />
          <button
            className="text-[color:var(--ink)]"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[color:var(--line-soft)] bg-[var(--bg)] px-6 py-5 space-y-4" data-testid="nav-mobile-panel">
          {PRIMARY_LINKS.map((l) => (
            <NavLink
              key={`m-${l.testid}`}
              to={l.to}
              end={l.end}
              className="block text-[color:var(--ink-2)]"
              onClick={() => setOpen(false)}
            >
              {t(l.key)}
            </NavLink>
          ))}
          {user ? (
            <>
              <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="block text-[color:var(--ink)]" onClick={() => setOpen(false)}>
                {user.role === "admin" ? t("nav.studio") : t("nav.your_space")}
              </Link>
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="btn-ghost w-full justify-center">{t("nav.sign_out")}</button>
            </>
          ) : (
            <Link to="/login" className="btn-ghost w-full justify-center" onClick={() => setOpen(false)}>{t("nav.client_login")}</Link>
          )}
        </div>
      )}
    </header>
  );
}
